import React, { useCallback, useContext, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';

import { AddFundsErrorSection } from './error-section';
import { FundLimitBanner } from './limit-banner';
import styles from './styles.module.less';
import FormattedCurrencyContainer from '../../../../../components/formatted-currency/container';
import PhoneVerificationBanner from '../../../../../components/phone-verification/banner-message';
import { PhoneVerificationContext } from '../../../../../components/phone-verification/context';
import { FundsSummary } from '../../../../../financial-services-ui/surcharge/funds-summary';
import { RetrySection } from '../../../../../financial-services-ui/surcharge/retry-section';
import { useShouldAllowPurchase } from '../../../../../hooks/account-information/use-fund-limit-remaining';
import { useIsAccountLimited } from '../../../../../hooks/account-information/use-show-limit-information';
import { useAddFundsContext } from '../../context/add-funds-context-provider';
import { MINIMUM_PURCHASE_AMT, ZERO_BALANCE } from '../constants/add-funds-constants';
import { AddFundButtons } from '../funding/add-fund-buttons';
import messages from '../funding/messages';

/**
 * @param {object} props
 * @param {string} props.sellerCurrencyCode
 * @param {number} [props.balance]
 * @param {boolean} [props.allowDefaultPurchase]
 * @param {(isDisabled: boolean) => void} props.setSaveIsDisabled
 * @returns {JSX.Element}
 */
export const AddFundsModalBody = ({ sellerCurrencyCode, balance = ZERO_BALANCE, allowDefaultPurchase = false, setSaveIsDisabled }) => {
  const {
    submitting,
    setFundAmount,
    fundAmount,
    useAsDefault,
    setUseAsDefault,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    totalWithFees,
    previewError,
    isSurchargeLoading,
    triggerRetry,
  } = useAddFundsContext();

  const updateFundAmount = useCallback(
    (value) => {
      setFundAmount(value);
      if (value < MINIMUM_PURCHASE_AMT) {
        setSaveIsDisabled(true);
      }
      if (value >= MINIMUM_PURCHASE_AMT) {
        setSaveIsDisabled(false);
      }
    },
    [setFundAmount, setSaveIsDisabled],
  );

  const { providerIdRequiringPhoneVerification } = useContext(PhoneVerificationContext);
  const toggleUseAsDefault = useCallback(() => setUseAsDefault((val) => !val), [setUseAsDefault]);
  const shouldAllowPurchaseByLimit = useShouldAllowPurchase(fundAmount);
  const showLimitBanner = useIsAccountLimited();
  const [isOtherAmountVisible, setIsOtherAmountVisible] = useState(false);

  return (
    <div className={styles.body}>
      <AddFundsErrorSection currencyCode={balance?.code} />
      {providerIdRequiringPhoneVerification ? (
        <PhoneVerificationBanner sellerLabelProviderId={providerIdRequiringPhoneVerification} />
      ) : null}
      {showLimitBanner ? <FundLimitBanner /> : null}
      <div className={styles.title}>
        <FormattedMessage {...messages.addFundsModalTitle} />
        <div
          className={classnames(styles.amount, {
            [styles['zero-balance']]: balance === ZERO_BALANCE,
          })}
        >
          <FormattedCurrencyContainer currencyCode={sellerCurrencyCode} fractionDigits={2} value={balance} />
        </div>
      </div>
      <div className={styles['buttons-container']}>
        <FormattedMessage {...messages.addFundsNote} />
        <AddFundButtons
          classNames={classnames(styles['currency-input'], {
            [styles['error-border']]: !shouldAllowPurchaseByLimit,
          })}
          disabled={submitting}
          fundAmount={fundAmount}
          onChangeAction={updateFundAmount}
          onClickAction={updateFundAmount}
          onOtherButtonVisibilityChange={setIsOtherAmountVisible}
          sellerCurrencyCode={sellerCurrencyCode}
        />
      </div>
      {!previewError ? (
        <FundsSummary
          baseAmount={fundAmount}
          className={styles.summary}
          creditCardProcessingFee={creditCardProcessingFee}
          creditCardProcessingFeePercentage={creditCardProcessingFeePercentage}
          currencyCode={sellerCurrencyCode}
          totalWithFees={totalWithFees}
        />
      ) : (
        <RetrySection isLoading={isSurchargeLoading} triggerRetry={triggerRetry} />
      )}
      {allowDefaultPurchase && isOtherAmountVisible ? (
        <Checkbox
          checked={useAsDefault}
          className={styles['default-checkbox']}
          disabled={submitting || !shouldAllowPurchaseByLimit}
          label={<FormattedMessage {...messages.makeDefault} />}
          onChange={toggleUseAsDefault}
        />
      ) : null}
    </div>
  );
};
